import { useMemo } from 'react'
import moment from 'moment'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import { getEffectiveFlightPrice } from 'lib/flights/flightUtils'
import {
  getFnfSeatsLeftFromJourney,
  getPromotionSeatsLeftFromJourney,
  getSeatsLeftFromJourney,
  isRoundTripFnfPriceApplied,
  useSeatEligiblePassengers,
} from './utils'
import type { FlightsClassTypes } from 'constants/flight'

/**
 * Custom hook to fetch flight data for a single calendar item using charter flight journeys
 *
 * This hook uses journey prices directly from useAllCharterFlightJourneys:
 * - Uses useAllCharterFlightJourneys to get all flights (single API call, cached)
 * - Matches journeys by checkIn/checkOut dates
 * - Uses journey prices directly (no fare family upsell calls)
 * - Extracts seatsLeft from journey data
 *
 * @param params - Flight search parameters including checkIn/checkOut dates
 * @returns Flight data including fetching state, prices, and journey
 */
export function useFlightItemData(params: {
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  charterBundleRoute?: App.CharterBundleRoute
  /**
   * Forwarded to /flightsearch via useAllCharterFlightJourneys so this hook
   * shares a Redux cache key with feeder-aware callers.
   */
  feederOriginAirportCode?: string
  /**
   * When true, suppresses the /flightsearch dispatch. Use when the caller
   * already has prefetched flight data and no new request should fire.
   */
  disabled?: boolean
}): {
  fetching: boolean
  totalFlightFare: number
  originalTotalFlightFare: number
  departingFlight?: App.JourneyV2
  seatsLeft?: number
  promotionSeatsLeft?: number
  isPromotionPriceApplied?: boolean
  fnfSeatsLeft?: number
  isFnfPriceApplied: boolean
  returningFlight?: App.JourneyV2
} {
  const { checkIn, checkOut, rooms, fareClass, charterBundleRoute, feederOriginAirportCode, disabled = false } = params
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)

  // Use useAllCharterFlightJourneys directly - no fare family upsell calls
  // Results are cached, so multiple calls with same params won't trigger additional API requests
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass,
    occupancies: rooms,
    disabled: disabled || !charterBundleRoute,
    feederOriginAirportCode,
  })

  const fetchingJourneys = flightResults?.fetching
  const journeysError = flightResults?.error
  const searchResultsTimestamp = flightResults?.timestamp

  // Match departing journey by checkIn date (arrival date)
  const departingJourney = useMemo(() => {
    if (!checkIn || !flightResults?.fares?.[0]) return undefined
    return flightResults.fares[0].find(journey => {
      const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
      return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkIn
    })
  }, [checkIn, flightResults?.fares])

  // Match returning journey by checkOut date (departure date)
  const returningJourney = useMemo(() => {
    if (!checkOut || !flightResults?.fares?.[1]) return undefined
    return flightResults.fares[1].find(journey => {
      const departingDate = journey.flightGroup.flights.at(0)?.departingDate
      return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOut
    })
  }, [checkOut, flightResults?.fares])

  // Calculate flight prices and seatsLeft using journey prices directly
  const flightPricing = useMemo(() => {
    let totalFlightFare = 0
    let originalTotalFlightFare = 0

    // Get minimum seatsLeft from both departing and returning flights
    const departingSeatsLeft = getSeatsLeftFromJourney(departingJourney)
    const returningSeatsLeft = getSeatsLeftFromJourney(returningJourney)
    const seatsLeft = departingSeatsLeft !== undefined && returningSeatsLeft !== undefined
      ? Math.min(departingSeatsLeft, returningSeatsLeft)
      : departingSeatsLeft ?? returningSeatsLeft
    const departingPromotionSeatsLeft = getPromotionSeatsLeftFromJourney(departingJourney)
    const returningPromotionSeatsLeft = getPromotionSeatsLeftFromJourney(returningJourney)
    const promotionSeatsLeft = Math.max(departingPromotionSeatsLeft ?? 0, returningPromotionSeatsLeft ?? 0)
    const departingFnfSeatsLeft = getFnfSeatsLeftFromJourney(departingJourney)
    const returningFnfSeatsLeft = getFnfSeatsLeftFromJourney(returningJourney)
    const fnfSeatsLeft = departingFnfSeatsLeft !== undefined && returningFnfSeatsLeft !== undefined
      ? Math.min(departingFnfSeatsLeft, returningFnfSeatsLeft)
      : departingFnfSeatsLeft ?? returningFnfSeatsLeft
    const isFnfPriceApplied = isRoundTripFnfPriceApplied(
      departingJourney,
      returningJourney,
      seatEligiblePassengers,
    )
    const isPromotionPriceApplied = !isFnfPriceApplied && !!(
      departingJourney &&
      returningJourney &&
      (departingJourney.promotionPrice || returningJourney.promotionPrice)
    )

    if (departingJourney && returningJourney) {
      const departingFare = isFnfPriceApplied
        ? (getEffectiveFlightPrice(departingJourney, seatEligiblePassengers).all.totalFare ?? 0)
        : (departingJourney.price.all.totalFare ?? 0)
      const returningFare = isFnfPriceApplied
        ? (getEffectiveFlightPrice(returningJourney, seatEligiblePassengers).all.totalFare ?? 0)
        : (returningJourney.price.all.totalFare ?? 0)
      const feederEstimatedTotal = (departingJourney.feederEstimatedPrice ?? 0) + (returningJourney.feederEstimatedPrice ?? 0)
      totalFlightFare = departingFare + returningFare + feederEstimatedTotal
      originalTotalFlightFare = (departingJourney.price.all.totalFare ?? 0) + (returningJourney.price.all.totalFare ?? 0) + feederEstimatedTotal
    }

    // Determine if still fetching
    const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
    const isFetching = fetchingJourneys || (!hasSearchCompleted && !departingJourney && !returningJourney)

    return {
      fetching: isFetching,
      totalFlightFare,
      originalTotalFlightFare,
      departingFlight: departingJourney,
      seatsLeft,
      promotionSeatsLeft,
      isPromotionPriceApplied,
      fnfSeatsLeft,
      isFnfPriceApplied,
      returningFlight: returningJourney,
    }
  }, [departingJourney, returningJourney, fetchingJourneys, searchResultsTimestamp, journeysError, seatEligiblePassengers])

  return flightPricing
}
